import ModalFooter from "@/modules/common/components/ui-kit/modals/modal-footer";
import ModalHeader from "@/modules/common/components/ui-kit/modals/modal-header";
import { GlobalStyles } from "@mui/material";
import { Sheet, SheetRef } from "react-modal-sheet";
import { useEffect, useRef, useState } from "react";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";

export interface MobileModalProps {
  open: boolean;
  onClose: (...args: any[]) => any;
  children: React.ReactNode;
  title?: string;
  headerAction?: React.ReactNode;
  cancelButtonText?: string;
  confirmButtonText?: string;
  extraButtonText?: string;
  snapPoints?: number[];
  initialSnap?: number;
  onConfirm?: (...args: any[]) => any;
  onCancel?: (...args: any[]) => any;
  onExtra?: (...args: any[]) => any;
  isLoading?: boolean;
  isConfirmDisabled?: boolean;
  zIndex?: number;
}

const MobileModal = ({
  open,
  onClose,
  children,
  title,
  headerAction,
  cancelButtonText,
  confirmButtonText,
  extraButtonText,
  snapPoints,
  initialSnap = 0,
  onConfirm,
  onCancel,
  onExtra,
  isLoading,
  isConfirmDisabled,
  zIndex,
}: MobileModalProps) => {
  const sheetRef = useRef<SheetRef>(null);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  /**
   * §MobileQA (iOS Safari): how much of the LAYOUT viewport the software keyboard is covering.
   *
   * The old fallback watched `window.resize` / `window.innerHeight`. iOS never moves those for the
   * keyboard — it leaves the layout viewport alone and shrinks only the VISUAL one — so the event
   * never fired, the inset stayed 0, and the bottom-anchored sheet sat *underneath* the keyboard.
   * On a 390pt phone the keyboard plus its accessory bar eats ~370pt and the escalate sheet is
   * ~320pt: tapping "Escalation note" swallowed the title, the note field and both buttons, and
   * left the agent looking at a bare drag handle with no way to finish or see what they typed.
   *
   * `innerHeight - (visualViewport.height + offsetTop)` compares the two viewports at the same
   * instant, so it is immune to Safari's collapsing toolbar — which moves `innerHeight` on its own
   * and poisoned the captured-baseline approach — and it folds in the pan iOS applies to keep the
   * focused field visible. That pan matters: the sheet is `fixed`, i.e. anchored to the layout
   * viewport, so a pan the sheet does not know about pushes it off-screen. Chromium reports the
   * same way under `virtualKeyboard.overlaysContent`; without the flag it resizes the layout
   * viewport itself and the same formula correctly returns ~0.
   */
  useEffect(() => {
    if (!open) return;

    const viewport = window.visualViewport;
    if (!viewport) return;

    // Chromium: hold the layout viewport still, otherwise the reading below collapses to 0.
    const vk =
      typeof navigator !== "undefined" && "virtualKeyboard" in navigator
        ? (navigator.virtualKeyboard as any)
        : null;
    const previousOverlays = vk?.overlaysContent;
    if (vk) vk.overlaysContent = true;

    const read = () => {
      const inset = window.innerHeight - (viewport.height + viewport.offsetTop);
      // sub-pixel noise from the pan animation would otherwise leave a 1px lift behind
      setKeyboardHeight(inset > 1 ? inset : 0);
    };

    read();
    viewport.addEventListener("resize", read);
    // moving between fields pans without resizing, so scroll has to be watched too
    viewport.addEventListener("scroll", read);

    return () => {
      viewport.removeEventListener("resize", read);
      viewport.removeEventListener("scroll", read);
      if (vk) vk.overlaysContent = previousOverlays ?? false;
      setKeyboardHeight(0);
    };
  }, [open]);

  return (
    <>
      {/* §MobileQA (iOS Safari): `vh` on iOS resolves against the LARGE viewport — the height the
          page would have if the browser toolbar were retracted. The sheet is anchored to the
          bottom, so a 90vh sheet spills its last ~15% underneath Safari's toolbar, and the
          footer buttons ("Save plan", "Print document") are what lands there. `dvh` tracks the
          viewport that is actually visible, so the sheet always ends above the toolbar. Kept as an
          @supports override so the 90vh inline style stays the fallback on older engines.
          The safe-area inset covers the home indicator on notched devices. */}
      <GlobalStyles
        styles={{
          "@supports (height: 100dvh)": {
            ".react-modal-sheet-container": { maxHeight: "90dvh !important" },
          },
        }}
      />
      <Sheet
        ref={sheetRef}
        isOpen={open}
        onClose={onClose}
        snapPoints={snapPoints}
        initialSnap={initialSnap}
        style={{ zIndex: zIndex || nawyTheme.zIndex.modal }}
      >
      <Sheet.Container
        style={{
          backgroundColor: nawyTheme.nawy.colors.semantic.layer.primary,
          borderTopLeftRadius: nawyTheme.nawy.radius["2xl"],
          borderTopRightRadius: nawyTheme.nawy.radius["2xl"],
          // The home indicator sits over the KEYBOARD once it is up, so the two insets never
          // stack — adding both left a dead 34px band between the sheet and the keys.
          paddingBottom:
            keyboardHeight > 0
              ? `${keyboardHeight}px`
              : "env(safe-area-inset-bottom, 0px)",
          // that padding has to eat into the 90dvh cap, not extend past it, or a tall sheet
          // grows off the top of the screen instead of scrolling
          boxSizing: "border-box",
          ...(snapPoints ? {} : { height: "auto", maxHeight: "90vh" }),
        }}
      >
        <Sheet.Header
          style={{
            backgroundColor: nawyTheme.nawy.colors.semantic.layer.primary,
            borderTopLeftRadius: nawyTheme.nawy.radius["2xl"],
            borderTopRightRadius: nawyTheme.nawy.radius["2xl"],
          }}
        />

        {/* §MobileQA (2026-08-16): the sheet OWNS the scroll. The old no-snapPoints chain gave the
            content box `height: auto` — it grew to the full content height, its own overflow never
            engaged, and the 90dvh container simply CLIPPED it: tall sheets (Request documents,
            Upcoming actions) lost their bottom rows with no way to scroll to them. A plain Box
            scroller also fought the sheet's drag-to-close gesture for touch moves; Sheet.Scroller
            is the library's touch-aware scroller (drag-to-close only from the scroll top). Header
            and footer sit OUTSIDE it, so the actions always stay visible above the content. */}
        <Sheet.Content
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            ...(snapPoints ? { height: "100%" } : { height: "auto", maxHeight: "100%" }),
          }}
        >
          {title && (
            <ModalHeader
              title={title}
              onClose={onClose}
              action={headerAction}
            />
          )}

          <Sheet.Scroller
            draggableAt="top"
            style={{
              flex: "1 1 auto",
              minHeight: 0,
              padding: nawyTheme.nawy.spacing.md,
            }}
          >
            {children}
          </Sheet.Scroller>

          {(cancelButtonText || confirmButtonText) && (
            <ModalFooter
              cancelButtonText={cancelButtonText}
              confirmButtonText={confirmButtonText}
              extraButtonText={extraButtonText}
              onCancel={onCancel ?? onClose}
              onConfirm={onConfirm}
              onExtra={onExtra}
              isLoading={isLoading}
              isConfirmDisabled={isConfirmDisabled}
            />
          )}
        </Sheet.Content>
      </Sheet.Container>
        <Sheet.Backdrop onTap={onClose} />
      </Sheet>
    </>
  );
};

export default MobileModal;
